import { useMemo, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, UserCog } from 'lucide-react';
import { School, SchoolSortField, SchoolsApi } from '@/services/superadmin.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { OwnerModal } from '@/components/superadmin/OwnerModal';

/**
 * Dedicated cross-school "who administers each school" screen — the old
 * system's "Admin Users" sidebar item (under Schools). Each school's admin
 * is a TENANT user (role `owner`, in that school's own schema) — a
 * DIFFERENT concept from `organization_admins`/org_users, which govern an
 * organization admin across several schools and have no management screen
 * of their own yet. See docs/DEVIATIONS.md.
 *
 * Reuses the existing get/set-owner endpoints and the shared `OwnerModal`
 * (also used inline on SchoolsPage) — this screen just gives that
 * capability its own focused, searchable home instead of requiring HO to
 * open each school's edit form to find it.
 */
const columnHelper = createColumnHelper<School>();

export function AdminUsersPage() {
  const qc = useQueryClient();
  const [ownerTarget, setOwnerTarget] = useState<School | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const dt = useDataTable({ defaultLimit: 20 });

  const { data, isFetching } = useQuery({
    queryKey: ['admin-users', dt.params],
    queryFn: () =>
      SchoolsApi.list({ ...dt.params, sortBy: dt.params.sortBy as SchoolSortField | undefined }),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'School',
        enableSorting: true,
        cell: (info) => {
          const s = info.row.original;
          return (
            <div className="leading-tight">
              <div className="font-medium text-slate-900">{s.name}</div>
              <div className="text-xs text-slate-500">{s.slug}</div>
            </div>
          );
        },
      }),
      columnHelper.accessor('adminName', {
        id: 'adminName',
        header: 'Admin',
        enableSorting: true,
        cell: (info) => {
          const s = info.row.original;
          if (!s.adminName && !s.adminEmail) {
            return <Badge tone="amber">No admin assigned</Badge>;
          }
          return (
            <div className="text-sm leading-snug">
              <div className="font-medium text-slate-800">{s.adminName ?? '—'}</div>
              <div className="text-slate-500">{s.adminEmail ?? ''}</div>
            </div>
          );
        },
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'School status',
        enableSorting: true,
        cell: (info) => <Badge tone="slate">{info.getValue()}</Badge>,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: (info) => {
          const s = info.row.original;
          return (
            <button
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
              onClick={() => setOwnerTarget(s)}
            >
              <UserCog className="h-3.5 w-3.5" />
              {s.adminName || s.adminEmail ? 'Manage' : 'Assign admin'}
            </button>
          );
        },
      }),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Admin Users"
        description="Every school and who administers it. Assign, view, or reset a school admin's login."
      />

      {notice && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-green-600 hover:text-green-800">
            ✕
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No schools onboarded yet."
        toolbar={
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={dt.searchInput}
              onChange={(e) => dt.setSearchInput(e.target.value)}
              placeholder="Search school, admin name/email…"
              className="pl-9"
            />
          </div>
        }
      />

      {ownerTarget && (
        <OwnerModal
          school={ownerTarget}
          onClose={() => setOwnerTarget(null)}
          onSaved={(msg) => {
            setOwnerTarget(null);
            setNotice(msg);
            qc.invalidateQueries({ queryKey: ['admin-users'] });
          }}
        />
      )}
    </>
  );
}
